import { BeconAccountTypes, BeconCommonTypes, BeconSkinTypes, BeconUserTypes } from "@becon-team/becon-fe-lib/my-types";
import { zDateString } from "@becon-team/becon-fe-lib/my-zod";
import { z } from "zod";

export const UserInResultDtoSchema = z.object({
  user_id: z.string().optional().nullable(),
  nickname: z.string().optional().nullable(),
  gender: z.nativeEnum(BeconUserTypes.UserGenderEnum).optional().nullable(),
  age_group: z.nativeEnum(BeconUserTypes.AgeGroupEnum).optional().nullable(),
  hair_loss_type: z.nativeEnum(BeconUserTypes.HairlossTypeEnum).optional().nullable(),
  hair_loss_level: z.number().optional().nullable(),
  hairloss_family_history: z.nativeEnum(BeconUserTypes.HairlossFamilyHistoryEnum).optional().nullable(),
  scalp_issue: z
    .object({
      issue_1: z.nativeEnum(BeconUserTypes.ScalpIssueTypeEnum).optional().nullable(),
      issue_2: z.nativeEnum(BeconUserTypes.ScalpIssueTypeEnum).optional().nullable(),
      issue_3: z.nativeEnum(BeconUserTypes.ScalpIssueTypeEnum).optional().nullable(),
      level_1: z.number().optional().nullable(),
      level_2: z.number().optional().nullable(),
      level_3: z.number().optional().nullable(),
    })
    .optional()
    .nullable(),
  hair_damage_level: z.number().optional().nullable(),
});
export type UserInResultDto = z.infer<typeof UserInResultDtoSchema>;

export const HeadquarterInResultDtoSchema = z.object({
  id: z.number().optional().nullable(),
  name: z.string().optional().nullable(),
  code: z.string().optional().nullable(),
});
export type HeadquarterInResultDto = z.infer<typeof HeadquarterInResultDtoSchema>;

export const CompanyInResultDtoSchema = z.object({
  id: z.number().optional().nullable(),
  name: z.string().optional().nullable(),
  code: z.string().optional().nullable(),
  region: z.nativeEnum(BeconCommonTypes.RegionEnum).optional().nullable(),
});
export type CompanyInResultDto = z.infer<typeof CompanyInResultDtoSchema>;

// TODO: 타입 고정시, 라이브러리로 이관 필요
export enum SkinAnalysisTypeEnum {
  MICRO = "MICRO",
  MACRO = "MACRO",
  COMPOSITE = "COMPOSITE",
}
export type SkinAnalysisType = keyof typeof SkinAnalysisTypeEnum;

export enum SkinMeasureItemEnum {
  PIGMENTATION = "PIGMENTATION",
  ACNE = "ACNE",
  WRINKLE = "WRINKLE",
  DARK_CIRCLE = "DARK_CIRCLE",
  TEMPERATURE = "TEMPERATURE",
  MOISTURE = "MOISTURE",
  SEBUM = "SEBUM",
  PORPHYRIN = "PORPHYRIN",
  PORE_SIZE = "PORE_SIZE",
  PORE_DENSITY = "PORE_DENSITY",
  SENSITIVITY = "SENSITIVITY",
}
export type SkinMeasureItem = keyof typeof SkinMeasureItemEnum;

export enum SkinImageBorderColorEnum {
  WHITE = "WHITE",
  RED = "RED",
  YELLOW = "YELLOW",
  GREEN = "GREEN",
}
export type SkinImageBorderColor = keyof typeof SkinImageBorderColorEnum;

export enum SkinTypeEnum {
  GENERAL = "GENERAL",
  DRY = "DRY",
  OILY = "OILY",
  SENSITIVITY = "SENSITIVITY",
  TROUBLE = "TROUBLE",
  COMBINATION = "COMBINATION",
}
export type SkinType = keyof typeof SkinTypeEnum;

export enum SkinCareEnum {
  SENSITIVITY = "SENSITIVITY",
  OILY = "OILY",
  ACNE_TROUBLE = "ACNE_TROUBLE", // 여드름/트러블
  PORE = "PORE",
  PIGMENTATION = "PIGMENTATION", // 색소침착
  DARK_CIRCLE = "DARK_CIRCLE",
  BLEMISHES = "BLEMISHES", // 기미/잡티
  WRINKLE = "WRINKLE",
  DRY = "DRY",
  DEAD_SKIN = "DEAD_SKIN",
  SEBUM = "SEBUM",
  BLACKHEAD = "BLACKHEAD",
  ACNE_SCAR = "ACNE_SCAR", // 여드름 흉터
  ELASTICITY = "ELASTICITY", //탄력성
  TEMPERATURE = "TEMPERATURE",
}
export type SkinCare = keyof typeof SkinCareEnum;

export enum SkinMicroMeasureItemEnum {
  SENSITIVITY = "SENSITIVITY",
  PORE_DENSITY = "PORE_DENSITY",
  PORE_SIZE = "PORE_SIZE",
  SEBUM = "SEBUM",
  PORPHYRIN = "PORPHYRIN",
}

export type SkinMicroMeasureItem = keyof typeof SkinMicroMeasureItemEnum;

export enum SkinMicroPositionEnum {
  CHEEK = "CHEEK",
  CHIN = "CHIN",
  FOREHEAD = "FOREHEAD",
  NOSE = "NOSE",
}

export type SkinMicroItem = keyof typeof SkinMicroPositionEnum;

// ========== analysis ========== //
export const SkinMicroCandidateDtoSchema = z.object({
  id: z.number(),
  image: z.string(),
  position: z.nativeEnum(BeconSkinTypes.SkinMicroPositionEnum),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  reg_date: z.string(),
});
export type SkinMicroCandidateDto = z.infer<typeof SkinMicroCandidateDtoSchema>;

export const SkinMacroCandidateDtoSchema = z.object({
  id: z.number(),
  image: z.string(),
  position: z.nativeEnum(BeconSkinTypes.SkinMacroPositionEnum),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  reg_date: z.string(),
});
export type SkinMacroCandidateDto = z.infer<typeof SkinMacroCandidateDtoSchema>;

// ========== result ========== //
const SkinMicroAnalysiszedImageDtoSchema = z.object({
  normal: z.string(),
  uv: z.string().nullable(),
});

const SkinSensorMeasurementItemDtoSchema = z.object({
  all_average: z.number(),
  my_average: z.number(),
});

const SkinMacroItemDtoSchema = z.object({
  all_average: z.number(),
  my_average: z.number(),
  images: z.array(
    z.object({
      position: z.nativeEnum(BeconSkinTypes.SkinMacroPositionEnum),
      source: z.string(),
    }),
  ),
});

const SkinMicroItemDtoSchema = z.object({
  all_average: z.number(),
  my_average: z.number(),
  image: z.object({
    cheek: z
      .object({
        image_url: z.array(
          z.object({
            image_url: z.string(),
            sensor_image_url: z.string().optional(),
          }),
        ),
        all_average: z.number(),
        my_average: z.number(),
      })
      .optional(),
    nose: z
      .object({
        image_url: z.array(
          z.object({
            image_url: z.string(),
            sensor_image_url: z.string().optional(),
          }),
        ),
        all_average: z.number(),
        my_average: z.number(),
      })
      .optional(),
    chin: z
      .object({
        image_url: z.array(
          z.object({
            image_url: z.string(),
            sensor_image_url: z.string().optional(),
          }),
        ),
        all_average: z.number(),
        my_average: z.number(),
      })
      .optional(),
    forehead: z
      .object({
        image_url: z.array(
          z.object({
            image_url: z.string(),
            sensor_image_url: z.string().optional(),
          }),
        ),
        all_average: z.number(),
        my_average: z.number(),
      })
      .optional(),
  }),
  macro_image: z.string().nullable(),
});

const SkinMicroPositionScoreDtoSchema = z.object({
  score: z.number(),
  images: z.array(
    z.object({
      normal: z.string(),
      uv: z.string(),
    }),
  ),
});

const SkinMeasurementPresentScoreSchema = z.object({
  // macro
  pigmentation: z.number().optional(), // 기미/잡티
  acne: z.number().optional(), //여드름
  dark_circle: z.number().optional(), //다크서클
  wrinkle: z.number().optional(), //주름
  // always
  moisture: z.number().optional(), //수분
  temperature: z.number().optional(), //피부열
  // micro
  sensitivity: z.number().optional(), //홍조
  pore_density: z.number().optional(), // 모공밀도
  pore_size: z.number().optional(), //모공크기
  sebum: z.number().optional(), //피지
  porphyrin: z.number().optional(), //포피린
});
export type SkinMeasurementPresentScoreDto = z.infer<typeof SkinMeasurementPresentScoreSchema>;
export type SkinMeasurementPresentScoreKey = keyof SkinMeasurementPresentScoreDto;

const SkinMacroPositionColorImagesSchema = z.object({
  nose: z.array(
    z.object({
      color: z.nativeEnum(SkinImageBorderColorEnum),
      image: z.string(),
    }),
  ),
  cheek: z.array(
    z.object({
      color: z.nativeEnum(SkinImageBorderColorEnum),
      image: z.string(),
    }),
  ),
  chin: z.array(
    z.object({
      color: z.nativeEnum(SkinImageBorderColorEnum),
      image: z.string(),
    }),
  ),
  forehead: z.array(
    z.object({
      color: z.nativeEnum(SkinImageBorderColorEnum),
      image: z.string(),
    }),
  ),
  eye: z.array(
    z.object({
      color: z.nativeEnum(SkinImageBorderColorEnum),
      image: z.string(),
    }),
  ),
});
export type SkinMacroPositionColorImagesSchemaDto = z.infer<typeof SkinMacroPositionColorImagesSchema>;

export const SkinMicroResultDtoSchema = z.object({
  analysis_images: z.object({
    nose: z.array(SkinMicroAnalysiszedImageDtoSchema),
    cheek: z.array(SkinMicroAnalysiszedImageDtoSchema),
    chin: z.array(SkinMicroAnalysiszedImageDtoSchema),
    forehead: z.array(SkinMicroAnalysiszedImageDtoSchema),
  }),
  score_by_position: z.object({
    nose: SkinMicroPositionScoreDtoSchema.optional(),
    cheek: SkinMicroPositionScoreDtoSchema.optional(),
    chin: SkinMicroPositionScoreDtoSchema.optional(),
    forehead: SkinMicroPositionScoreDtoSchema.optional(),
  }),
  detail_items: z.object({
    sensitivity: SkinMicroItemDtoSchema,
    pore_density: SkinMicroItemDtoSchema,
    pore_size: SkinMicroItemDtoSchema,
    sebum: SkinMicroItemDtoSchema,
    porphyrin: SkinMicroItemDtoSchema,
    temperature: SkinMicroItemDtoSchema,
    moisture: SkinMicroItemDtoSchema,
  }),
  sensor_measurement_items: z.object({
    temperature: SkinSensorMeasurementItemDtoSchema,
    gas: SkinSensorMeasurementItemDtoSchema,
    moisture: SkinSensorMeasurementItemDtoSchema,
  }),
});

export type SkinMicroResultDto = z.infer<typeof SkinMicroResultDtoSchema>;

export const SkinAnalysisResultDtoSchema = z.object({
  id: z.number(),
  user_id: z.string(),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  elasticity_index: z.number().nullable(),
  over_view: z.object({
    composite_score: z.number(),
    group: z.object({
      age_group: z.nativeEnum(BeconUserTypes.AgeGroupEnum),
      gender: z.nativeEnum(BeconUserTypes.UserGenderEnum),
      num_records: z.number(),
      rank: z.number(),
      top_10: z.number(),
      composite_score_ratio: z.array(
        z.object({
          level: z.number(),
          num_records: z.number(),
        }),
      ),
      skin_type_ratio: z.object({
        combination: z.number(),
        dry: z.number(),
        general: z.number(),
        oily: z.number(),
        sensitivity: z.number(),
        trouble: z.number(),
      }),
      average_composite_score: z.number(),
    }),
  }),
  skin_types: z.object({
    main: z.nativeEnum(SkinTypeEnum),
    sub: z.nativeEnum(SkinTypeEnum).optional(),
    skin_cares: z.array(z.nativeEnum(SkinCareEnum)),
  }),
  measurement_score: z.object({
    all_average: SkinMeasurementPresentScoreSchema,
    my_average: SkinMeasurementPresentScoreSchema,
  }),
  macro_result: z
    .object({
      analysis_images: z.array(
        z.object({
          position: z.nativeEnum(BeconSkinTypes.SkinMacroPositionEnum),
          image_url: z.string(),
        }),
      ),
      position_color_images: SkinMacroPositionColorImagesSchema,
      detail_items: z.object({
        wrinkle: SkinMacroItemDtoSchema,
        dark_circle: SkinMacroItemDtoSchema,
        pigmentation: SkinMacroItemDtoSchema,
        acne: SkinMacroItemDtoSchema,
        skin_tone_v_2: z.object({
          similar: z.string(),
          my: z.array(
            z.object({
              color: z.string(),
              ratio: z.number(),
            }),
          ),
        }),
      }),
      masked_image: z.object({
        all_masked_image: z.string(),
        image: z.string(),
        position_score: z.object({
          nose: z.object({
            score: z.number(),
            acne_ratio: z.number(),
            pigmentation_ratio: z.number(),
            dark_circle_ratio: z.number(),
            wrinkle_ratio: z.number(),
          }),
          cheek: z.object({
            score: z.number(),
            acne_ratio: z.number(),
            pigmentation_ratio: z.number(),
            dark_circle_ratio: z.number(),
            wrinkle_ratio: z.number(),
          }),
          chin: z.object({
            score: z.number(),
            acne_ratio: z.number(),
            pigmentation_ratio: z.number(),
            dark_circle_ratio: z.number(),
            wrinkle_ratio: z.number(),
          }),
          forehead: z.object({
            score: z.number(),
            acne_ratio: z.number(),
            pigmentation_ratio: z.number(),
            dark_circle_ratio: z.number(),
            wrinkle_ratio: z.number(),
          }),
        }),
      }),
      forehead_analysis: z.object({}).nullable(),
    })
    .nullable(),
  micro_result: SkinMicroResultDtoSchema.nullable(),
  extras: z.object({
    headquarter: HeadquarterInResultDtoSchema.optional().nullable(),
    company: CompanyInResultDtoSchema.optional().nullable(),
    user: UserInResultDtoSchema.optional().nullable(),
  }),
  reg_date: z.string(),
  analysis_date: z.string().nullable(),
});
export type SkinAnalysisResultDto = z.infer<typeof SkinAnalysisResultDtoSchema>;

export enum SkinMicroSnakePositionEnum {
  nose = "nose",
  cheek = "cheek",
  chin = "chin",
  forehead = "forehead",
}
export type SkinMicroSnakePosition = keyof typeof SkinMicroSnakePositionEnum;

export const skinMicroItemSchema = z.object({
  type: z.nativeEnum(BeconSkinTypes.SkinMeasureItemEnum),
  myValue: z.number().nullish(),
  averageValue: z.number().nullish(),
  position: z.union([z.literal("nose"), z.literal("cheek"), z.literal("chin"), z.literal("forehead")]),
  imageUrls: z.array(z.string()).nullish(),
});
export const skinMicroItemsSchema = z.array(skinMicroItemSchema);

export type skinMicroItems = z.infer<typeof skinMicroItemsSchema>;
export type skinMicroItem = z.infer<typeof skinMicroItemSchema>;

// ========== inquiry ========== //
export enum SkinIssueDetailSelectorEnum {
  NOT_SURE = "NOT_SURE",
  LITTLE = "LITTLE",
  NO = "NO",
  NORMAL = "NORMAL",
  A_LOT = "A_LOT",
}
export type SkinIssueDetailSelector = keyof typeof SkinIssueDetailSelectorEnum;

export enum YesOrNoSelectorEnum {
  YES = "YES",
  NO = "NO",
}
export type YesOrNoSelector = keyof typeof YesOrNoSelectorEnum;

export enum SkinIssueEnum {
  NONE = "NONE", //없음
  WRINKLE = "WRINKLE", // 주름
  DARK_CIRCLE = "DARK_CIRCLE", // 다크서클
  ELASTICITY = "ELASTICITY", // 탄력 저하
  BLEMISHES = "BLEMISHES", // 기미/잡티
  ACNE = "ACNE", //여드름/트러블
  ACNE_SCAR = "ACNE_SCAR", // 여드름 흉터
  PORE_SIZE = "PORE_SIZE", // 모공 크기
  DEAD_SKIN = "DEAD_SKIN", // 각질
  DRY = "DRY", // 건조
  ITCH = "ITCH", // 가려움
  SEBUM = "SEBUM", // 피지 과다
  BLACKHEAD = "BLACKHEAD", // 블렉헤드
  SENSITIVITY = "SENSITIVITY", // 민감 피부
  WHITENING = "WHITENING", //미백
}
export type SkinIssue = keyof typeof SkinIssueEnum;

export enum SkinCareSelectorEnum {
  NONE = "NONE",
  ONE = "ONE",
  TWO = "TWO",
  THREE = "THREE",
  FOUR_OR_MORE = "FOUR_OR_MORE",
}
export type SkinCareSelector = keyof typeof SkinCareSelectorEnum;

export enum SleepTimeEnum {
  LESS_THAN_SIX = "LESS_THAN_SIX",
  MORE_THAN_SIX = "MORE_THAN_SIX",
}
export type SleepTime = keyof typeof SleepTimeEnum;

export enum StressLevelEnum {
  NONE = "NONE",
  LOW = "LOW",
  MIDDLE = "MIDDLE",
  HIGH = "HIGH",
}
export type StressLevel = keyof typeof StressLevelEnum;

export const SkinInquiryDtoSchema = z.object({
  skin_issue_detail: z
    .object({
      tight_after_wash_face: z.nativeEnum(SkinIssueDetailSelectorEnum),
      is_oily: z.nativeEnum(SkinIssueDetailSelectorEnum),
      is_dry: z.nativeEnum(SkinIssueDetailSelectorEnum),
    })
    .optional()
    .nullable(),
  issues: z.array(z.nativeEnum(SkinIssueEnum)).optional().nullable(),
  skin_care: z
    .object({
      product_count: z.nativeEnum(SkinCareSelectorEnum).optional().nullable(),
      use_body_moisture_product: z.nativeEnum(YesOrNoSelectorEnum).optional().nullable(),
    })
    .optional()
    .nullable(),
  life_style: z
    .object({
      sleep_time: z.nativeEnum(SleepTimeEnum).optional().nullable(),
      stress_level: z.nativeEnum(StressLevelEnum).optional().nullable(),
      diet: z.nativeEnum(YesOrNoSelectorEnum).optional().nullable(),
      smoking: z.nativeEnum(YesOrNoSelectorEnum).optional().nullable(),
      drinking: z.nativeEnum(YesOrNoSelectorEnum).optional().nullable(),
      sunscreen: z.nativeEnum(YesOrNoSelectorEnum).optional().nullable(),
      skin_care_periodically: z.nativeEnum(YesOrNoSelectorEnum).optional().nullable(),
    })
    .optional()
    .nullable(),
});
export type SkinInquiryDto = z.infer<typeof SkinInquiryDtoSchema>;

export const SkinHistoryStatisticsDtoSchema = z.object({
  items: z.array(
    z
      .object({
        average_score: z.number(),
        date: z.string(),
      })
      .nullable(),
  ),
  skip: z.number(),
  take: z.number(),
  total: z.number(),
});

export type SkinHistoryStatisticsDto = z.infer<typeof SkinHistoryStatisticsDtoSchema>;

export enum OrderByEnum {
  DESC = "DESC",
  ASC = "ASC",
}

export enum SkinSubSetEnum {
  MACRO = "MACRO",
  MICRO = "MICRO",
}

export enum GroupUnitEnum {
  DAY = "DAY",
  MONTH = "MONTH",
  YEAR = "YEAR",
}

export const GetSkinHistoryStatisticsRequestDtoSchema = z.object({
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  orderBy: z.nativeEnum(OrderByEnum).optional(),
  accountType: z.nativeEnum(BeconAccountTypes.AccountTypeEnum),
  subsetType: z.nativeEnum(SkinSubSetEnum),
  unit: z.nativeEnum(GroupUnitEnum).optional(),
  skip: z.number().optional(),
  take: z.number().optional(),
  timeOffset: z.string(),
});

export const GetSkinAnalysisSetRequestDtoSchema = z.object({
  fromDate: zDateString.optional(),
  toDate: zDateString.optional(),
  subsetType: z.nativeEnum(SkinSubSetEnum),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum).optional(),
  subsetStatus: z.nativeEnum(BeconCommonTypes.SetStatusEnum).optional(),
  accountType: z.nativeEnum(BeconAccountTypes.AccountTypeEnum).optional(),
  skip: z.number().optional(),
  take: z.number().optional(),
  timeOffset: z.string(),
});

export const SkinMicroTrendSchema = z.object({
  data: z.array(
    z.object({
      date: zDateString,
      score: z.number(),
    }),
  ),
  micro_position_type: z.nativeEnum(SkinMicroPositionEnum),
});

export type SkinMicroTrend = z.infer<typeof SkinMicroTrendSchema>;

export const SkinAverageScoreByMicroPositionSchema = z.object({
  image_data: z.array(
    z.object({
      normal_image: z.string(),
      uv_image: z.string(),
      my_score: z.number(),
    }),
  ),

  micro_position_type: z.nativeEnum(SkinMicroPositionEnum),
  my_score: z.number(),
  total_average: z.number(),
});

export type SkinAverageScoreByMicroPosition = z.infer<typeof SkinAverageScoreByMicroPositionSchema>;

export const SkinAverageScoreByMicroMeasureItemSchema = z.object({
  micro_measure_item_type: z.nativeEnum(SkinMicroMeasureItemEnum),
  my_score: z.number(),
  total_average_score: z.number(),
});
/** B2B */
export const SkinSensorItemB2BDtoSchema = z.object({
  all_average: z.number(),
  my_average: z.number(),
  my_score_average: z.number(),
});
export type SkinSensorItemB2BDto = z.infer<typeof SkinSensorItemB2BDtoSchema>;
export const SkinSensorItemsB2BDtoSchema = z.object({
  temperature: SkinSensorItemB2BDtoSchema,
  gas: SkinSensorItemB2BDtoSchema,
  moisture: SkinSensorItemB2BDtoSchema,
});
export type SkinSensorItemsB2BDto = z.infer<typeof SkinSensorItemsB2BDtoSchema>;
export const SkinMicroItemsB2BDtoSchema = z.object({
  sensitivity: SkinMicroItemDtoSchema,
  pore_density: SkinMicroItemDtoSchema,
  pore_size: SkinMicroItemDtoSchema,
  sebum: SkinMicroItemDtoSchema,
  porphyrin: SkinMicroItemDtoSchema,
  temperature: SkinMicroItemDtoSchema,
  moisture: SkinMicroItemDtoSchema,
});
export type SkinMicroItemsB2BDto = z.infer<typeof SkinMicroItemsB2BDtoSchema>;

export const SkinMicroResultB2BDtoSchema = z.object({
  analysis_images: z.object({
    nose: z.array(SkinMicroAnalysiszedImageDtoSchema),
    cheek: z.array(SkinMicroAnalysiszedImageDtoSchema),
    chin: z.array(SkinMicroAnalysiszedImageDtoSchema),
    forehead: z.array(SkinMicroAnalysiszedImageDtoSchema),
  }),
  score_by_position: z.object({
    nose: SkinMicroPositionScoreDtoSchema.optional(),
    cheek: SkinMicroPositionScoreDtoSchema.optional(),
    chin: SkinMicroPositionScoreDtoSchema.optional(),
    forehead: SkinMicroPositionScoreDtoSchema.optional(),
  }),
  detail_items: SkinMicroItemsB2BDtoSchema,
  sensor_measurement_items: SkinSensorItemsB2BDtoSchema,
});
export type SkinMicroResultB2BDto = z.infer<typeof SkinMicroResultB2BDtoSchema>;

export const UserInResultB2BDtoSchema = z.object({
  user_id: z.string().nullable().optional(),
  phone_number: z.string().nullable().optional(),
  region: z.nativeEnum(BeconCommonTypes.RegionEnum).nullable().optional(),
  nickname: z.string().nullable().optional(),
  gender: z.nativeEnum(BeconUserTypes.UserGenderEnum).nullable().optional(),
  age_group: z.nativeEnum(BeconUserTypes.AgeGroupEnum).nullable().optional(),
  hair_loss_type: z.nativeEnum(BeconUserTypes.HairlossTypeEnum).nullable().optional(),
  hair_loss_level: z.number().nullable().optional(),
  hairloss_family_history: z.nativeEnum(BeconUserTypes.HairlossFamilyHistoryEnum).nullable().optional(),
  scalp_issue: z
    .object({
      issue_1: z.nativeEnum(BeconUserTypes.ScalpIssueTypeEnum).nullable().optional(),
      issue_2: z.nativeEnum(BeconUserTypes.ScalpIssueTypeEnum).nullable().optional(),
      issue_3: z.nativeEnum(BeconUserTypes.ScalpIssueTypeEnum).nullable().optional(),
      level_1: z.number().nullable().optional(),
      level_2: z.number().nullable().optional(),
      level_3: z.number().nullable().optional(),
    })
    .optional()
    .nullable(),
  hair_damage_level: z.number().nullable().optional(),
  skin_color: z.nativeEnum(BeconUserTypes.SkinColorEnum).nullable().optional(),
  hair_color: z.nativeEnum(BeconUserTypes.HairColorEnum).nullable().optional(),
  hair_type: z.nativeEnum(BeconUserTypes.HairTypeEnum).nullable().optional(),
  lifestyle: z
    .object({
      dyeing: z.nativeEnum(BeconUserTypes.NoneOrPeriodicalEnum).nullable().optional(),
      perm: z.nativeEnum(BeconUserTypes.NoneOrPeriodicalEnum).nullable().optional(),
    })
    .nullable()
    .optional(),
});

export type UserInResultB2BDto = z.infer<typeof UserInResultB2BDtoSchema>;

const SkinOverViewSchema = z.object({
  group: z.object({
    rank: z.number(),
    gender: z.string(),
    age_group: z.string(),
    num_records: z.number(),
    average_composite_score: z.number(),
  }),
  skin_types: z.object({
    main: z.nativeEnum(BeconSkinTypes.SkinTypeEnum),
    sub: z.nativeEnum(BeconSkinTypes.SkinTypeEnum).optional(),
    skin_cares: z.array(z.nativeEnum(BeconSkinTypes.SkinCareEnum)),
  }),
  elasticity: z.object({
    index: z.number(),
    average_value: z.number(),
  }),
  my_composite_score: z.number(),
});

const SkinDetailItemCommonSchema = z.object({
  analysis_image_url: z.string(),
  origin_image_url: z.string(),
  my_value: z.number(),
  my_score: z.number(),
  all_average_value: z.number(),
  all_average_score: z.number(),
});

const SkinMacroDetailItemSchema = SkinDetailItemCommonSchema.merge(
  z.object({
    type: z.nativeEnum(BeconSkinTypes.SkinMacroItemEnum),
  }),
);
export type SkinMacroDetailItem = z.infer<typeof SkinMacroDetailItemSchema>;

const SkinPositionImageSchema = z.object({
  images_url: z.object({
    warning: z.string(),
    normal: z.string(),
    caution: z.string(),
  }),
  position: z.nativeEnum(BeconSkinTypes.SkinMicroPositionEnum),
  coordinates: z.object({
    x: z.number(),
    y: z.number(),
  }),
});

// NOTE: lib enum에 없는 MELANIN이 API 응답에 포함되어 임시 추가 (becon-fe-lib 업데이트 시 제거)
const SkinMicroDetailItemSchema = z.object({
  type: z.union([
    z.nativeEnum(BeconSkinTypes.SkinMicroItemEnum),
    z.nativeEnum(BeconSkinTypes.SkinSensorItemEnum),
    z.literal("MELANIN"),
  ]),
  positions: z.array(
    SkinDetailItemCommonSchema.merge(
      z.object({
        position: z.nativeEnum(BeconSkinTypes.SkinMicroPositionEnum),
      }),
    ),
  ),
  all_average_value: z.number(),
  all_average_score: z.number(),
});
export type SkinMicroDetailItem = z.infer<typeof SkinMicroDetailItemSchema>;

const SkinPositionAverageSchema = z.object({
  position: z.nativeEnum(BeconSkinTypes.SkinMicroPositionEnum),
  my_average_score: z.number(),
  my_average_value: z.number(),
  all_average_score: z.number(),
  all_average_value: z.number(),
});

const SkinMicroDetailItemV3Schema = SkinMicroDetailItemSchema.extend({
  positions_average: z.array(SkinPositionAverageSchema),
});
export type SkinMicroDetailItemV3 = z.infer<typeof SkinMicroDetailItemV3Schema>;

export const SkinAnalysisResultV3DtoSchema = z.object({
  user_id: z.string(),
  id: z.number(),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  analysis_date: zDateString.nullable(),
  over_view: SkinOverViewSchema,
  macro: z
    .object({
      origin_image_url: z.string(),
      analysis_image_url: z.string(),
      detail_items: z.array(SkinMacroDetailItemSchema),
      skin_tone: z.string(),
      position_images: z.array(SkinPositionImageSchema),
    })
    .nullable(),
  micro: z.object({
    detail_items: z.array(SkinMicroDetailItemV3Schema),
    ambient: z.object({
      humidity: z.number(),
      temperature: z.number(),
    }),
  }),
  extras: z.object({
    headquarter: HeadquarterInResultDtoSchema.nullable().optional(),
    company: CompanyInResultDtoSchema.nullable().optional(),
    user: UserInResultDtoSchema.nullable().optional(),
  }),
});
export type SkinAnalysisResultV3Dto = z.infer<typeof SkinAnalysisResultV3DtoSchema>;
